import { assertAmount } from './money';
import { RateError } from './errors';

/**
 * Rates are integer basis points. 5% is 500, 30% is 3000, 100% is 10000.
 *
 * Nothing in this package accepts a float rate. A float rate is how a ledger
 * acquires a fraction of a cent that nobody can account for, and how two
 * machines disagree about an allocation.
 */
export type BasisPoints = number;

export const BPS_SCALE = 10_000;

export function assertBasisPoints(bps: number, label = 'rate'): void {
  if (!Number.isSafeInteger(bps)) {
    throw new RateError(`${label} must be an integer number of basis points, got ${bps}`);
  }
  if (bps < 0 || bps > BPS_SCALE) {
    throw new RateError(`${label} must be within [0, ${BPS_SCALE}] basis points, got ${bps}`);
  }
}

/**
 * amount * bps / 10000, rounded half away from zero, computed entirely in
 * integers.
 *
 * Half away from zero rather than banker's rounding: banker's rounding is
 * better for repeated sums of independent values, but here the residual is
 * always assigned explicitly to a named party (see `split.ts`), so the tie
 * rule only needs to be stated, stable and easy to reproduce by hand when a
 * developer queries a statement line.
 */
export function multiplyByBasisPoints(amount: number, bps: BasisPoints): number {
  assertAmount(amount);
  assertBasisPoints(bps);

  const product = amount * bps;
  if (!Number.isSafeInteger(product)) {
    throw new RateError(
      `Intermediate product ${amount} * ${bps} is not exactly representable. ` +
        'This should be unreachable given MAX_MINOR_UNITS.',
    );
  }

  const quotient = Math.trunc(product / BPS_SCALE);
  const remainder = product - quotient * BPS_SCALE;

  if (remainder === 0) return quotient;

  // Round half away from zero. `remainder` carries the sign of `product`.
  if (Math.abs(remainder) * 2 >= BPS_SCALE) {
    return quotient + (product < 0 ? -1 : 1);
  }
  return quotient;
}

/** Converts a percentage that is known to be exact in basis points. Throws otherwise. */
export function percentToBasisPoints(percent: number): BasisPoints {
  const bps = Math.round(percent * 100);
  if (Math.abs(percent * 100 - bps) > 1e-9) {
    throw new RateError(
      `${percent}% is not expressible in whole basis points. Rates must be exact.`,
    );
  }
  assertBasisPoints(bps);
  return bps;
}

export function basisPointsToPercentString(bps: BasisPoints): string {
  assertBasisPoints(bps);
  const whole = Math.trunc(bps / 100);
  const fraction = bps - whole * 100;
  return fraction === 0 ? `${whole}%` : `${whole}.${String(fraction).padStart(2, '0')}%`;
}
